import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import { useAuth } from './AuthContext';

const BranchContext = createContext();
const STORAGE_KEY = 'pos_selected_branch_id';

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

/**
 * Branch context: fetches /api/branches once, persists selected branchId in localStorage.
 * For admin/super_admin: selected branch is stored and used for all branch-scoped API calls.
 * For manager: effectiveBranchId is always their single branch.
 */
export const BranchProvider = ({ children }) => {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchIdState] = useState(null);
  const [loading, setLoading] = useState(true);

  const setSelectedBranchId = useCallback((value) => {
    setSelectedBranchIdState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      if (next != null && next !== '') {
        try {
          localStorage.setItem(STORAGE_KEY, String(next));
        } catch (e) {
          // ignore
        }
      } else {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (e) {
          // ignore
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!user || !user.role) {
      setBranches([]);
      setSelectedBranchIdState(null);
      setLoading(false);
      return;
    }
    const fetchBranches = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/branches`);
        const list = res.data.branches || res.data.data?.branches || res.data || [];
        const arr = Array.isArray(list) ? list : [];
        setBranches(arr);
        const isManagerRole = ['manager', 'inventory_manager', 'accounts_manager'].includes(user.role);
        if (isManagerRole) {
          const bid = user.branch_id ?? user.branchId;
          setSelectedBranchIdState(bid ?? (arr[0]?.id ?? null));
        } else if ((user.role === 'admin' || user.role === 'super_admin') && arr.length > 0) {
          let stored = null;
          try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) stored = parseInt(raw, 10);
          } catch (e) {
            // ignore
          }
          const firstBranchId = arr[0].id ?? arr[0].branch_id;
          const validStored = stored && !isNaN(stored) && arr.some(b => (b.id ?? b.branch_id) === stored);
          const initialId = validStored ? stored : firstBranchId;
          setSelectedBranchIdState(initialId);
          try {
            if (initialId != null && initialId !== '') localStorage.setItem(STORAGE_KEY, String(initialId));
          } catch (e) {
            // ignore
          }
        }
      } catch (err) {
        console.error('Failed to fetch branches', err);
        setBranches([]);
        setSelectedBranchIdState(null);
      } finally {
        setLoading(false);
      }
    };
    fetchBranches();
  }, [user?.id, user?.role, user?.branch_id, user?.branchId]);

  const hasBranches = branches.length > 0;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isManager = ['manager', 'inventory_manager', 'accounts_manager'].includes(user?.role);
  const effectiveBranchId = isManager ? (user?.branch_id ?? user?.branchId) : selectedBranchId;
  const currentBranch = branches.find(b => (b.id ?? b.branch_id) === effectiveBranchId) || null;

  const value = {
    branches,
    selectedBranchId,
    setSelectedBranchId,
    hasBranches,
    effectiveBranchId,
    currentBranch,
    isAdmin,
    isManager,
    loading
  };

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
};
